import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import { styled } from '@mui/material/styles';
import stopCircleFill from '@iconify/icons-eva/stop-circle-fill';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';
import phoneCallFill from '@iconify/icons-eva/phone-call-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import downloadFill from '@iconify/icons-eva/download-fill';
import batteryFill from '@iconify/icons-eva/battery-fill';
import flashFill from '@iconify/icons-eva/flash-fill';
import fileTextFill from '@iconify/icons-eva/file-text-fill';
import bellFill from '@iconify/icons-eva/bell-fill';
import pinFill from '@iconify/icons-eva/pin-fill';
import { Icon } from '@iconify/react';
import { fDateTime } from 'src/utils/formatTime';
import { getActiveTransaction, getChargerDetails, sendOTPToEmail, verifyOTP, sendStopOTP, verifyStopOTP, getInvoiceByUpiTransactionId } from 'src/services/activeTransactionService';
import { useDomainConfig } from 'src/hooks/useDomainConfig';
import Page from '../components/Page';
import Logo from '../components/Logo';
import jsPDF from 'jspdf';

// Connector type mapping
const CONNECTOR_TYPES = [
  { type: "CCS 2", value: "connector1", id: 1 },
  { type: "Type 2", value: "connector2", id: 2 },
  { type: "CHAdeMo", value: "connector3", id: 3 },
  { type: "Type 1", value: "connector4", id: 4 },
  { type: "Type 1 AC", value: "connector5", id: 5 },
  { type: "3 Pin", value: "connector6", id: 6 },
  { type: "GBT", value: "connector7", id: 7 },
  { type: "CCS 1", value: "ccstype1", id: 8 },
  { type: "CCS/SAE", value: "ccssae", id: 9 },
  { type: "Three Phase", value: "threephase", id: 10 },
  { type: "Wall", value: "wall", id: 11 },
  { type: "J1772", value: "j1772", id: 12 },
  { type: "IEC60309", value: "ie60309", id: 13 },
  { type: "Type 6", value: "connector8", id: 14 },
  { type: "Type 7", value: "connector9", id: 15 },
];

// Function to get connector type name from value
const getConnectorTypeName = (connectorValue) => {
  const connector = CONNECTOR_TYPES.find(c => c.value === connectorValue);
  return connector ? connector.type : connectorValue;
};

// Function to get connector image path
const getConnectorImagePath = (connectorValue) => {
  return `/connectors/${connectorValue}.png`;
};

const RootStyle = styled(Page)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  padding: theme.spacing(1),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 50%, rgba(102, 126, 234, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(118, 75, 162, 0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1.5),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
  },
}));

const SearchCard = styled(Card)(({ theme }) => ({
  width: "100%",
  maxWidth: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  margin: theme.spacing(1, 'auto'),
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
  borderRadius: theme.shape.borderRadius * 2,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  },
  [theme.breakpoints.up('sm')]: {
    maxWidth: 600,
    margin: theme.spacing(2, 'auto'),
    borderRadius: theme.shape.borderRadius * 3,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
    },
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: 800,
  }
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  paddingTop: theme.spacing(1),
  position: 'relative',
  zIndex: 1,
  '& .logo': {
    maxWidth: 120,
    height: 'auto',
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08))',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.02)',
    },
    '& img': {
      width: '100%',
      height: 'auto',
      maxHeight: 36,
      objectFit: 'contain',
    }
  },
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(4),
    '& .logo': {
      maxWidth: 140,
      '& img': {
        maxHeight: 40,
      }
    }
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(4),
    '& .logo': {
      maxWidth: 160,
      '& img': {
        maxHeight: 44,
      }
    }
  }
}));

const InfoCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  boxShadow: `0 4px 16px ${theme.palette.grey[500_24]}`,
  borderRadius: theme.shape.borderRadius * 2,
  background: `linear-gradient(135deg, #1A73E8 0%, #34A853 50%, #0D47A1 100%)`,
  color: '#ffffff',
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid rgba(76, 175, 80, 0.3)`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.15) 50%, transparent 70%)`,
    pointerEvents: 'none',
  },
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 3,
    boxShadow: `0 8px 24px ${theme.palette.grey[500_24]}`,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 12px 32px rgba(26, 115, 232, 0.4)`,
    },
  },
}));

const DetailCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  borderRadius: theme.shape.borderRadius * 2,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 3,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
    },
  },
}));

const GridLabel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  background: alpha(theme.palette.primary.main, 0.03),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease',
  height: '100%',
  '& .label': {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.25),
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    lineHeight: 1.2,
  },
  '& .value': {
    fontSize: '0.9375rem',
    color: theme.palette.text.primary,
    fontWeight: 700,
    lineHeight: 1.3,
    wordBreak: 'break-word',
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius * 1.5,
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.06),
      borderColor: alpha(theme.palette.primary.main, 0.2),
      transform: 'translateY(-2px)',
    },
    '& .label': {
      fontSize: '0.875rem',
      marginBottom: theme.spacing(0.5),
    },
    '& .value': {
      fontSize: '1rem',
    },
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
    '& .label': {
      fontSize: '0.875rem',
      marginBottom: theme.spacing(0.75),
    },
    '& .value': {
      fontSize: '1.1rem',
    },
  },
}));

export default function ActiveTransaction() {
  const location = useLocation();
  const navigate = useNavigate();
  const domainConfig = useDomainConfig();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [transactions, setTransactions] = useState({}); // Store transactions per connector
  const [selectedTab, setSelectedTab] = useState(0);
  const selectedConnectorIdRef = useRef(null); // Track selected connector by ID to preserve selection
  const isInitialLoadRef = useRef(true); // Track if this is the initial load
  const prevChargerDetailsRef = useRef(null); // Track previous charger details to detect actual changes
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [stopError, setStopError] = useState('');
  const [stopPhoneNumber, setStopPhoneNumber] = useState('');
  const [stopPhoneError, setStopPhoneError] = useState('');
  const [stopOtpSent, setStopOtpSent] = useState(false);
  const [stopOtp, setStopOtp] = useState('');
  const [stopOtpError, setStopOtpError] = useState('');
  const [utrNumber, setUtrNumber] = useState('');
  const [utrNumberError, setUtrNumberError] = useState('');
  const [sendingStopOtp, setSendingStopOtp] = useState(false);
  const [verifyingStopOtp, setVerifyingStopOtp] = useState(false);
  const [stopConnectorId, setStopConnectorId] = useState(null);
  const [stopTransactionId, setStopTransactionId] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [chargerIdInput, setChargerIdInput] = useState('');
  const [chargerIdInputError, setChargerIdInputError] = useState('');
  const [chargerDetails, setChargerDetails] = useState(null);
  const [chargerLoading, setChargerLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false); // Manual refresh indicator
  const [transactionLoading, setTransactionLoading] = useState({}); // Loading state per connector
  const fetchingRef = useRef(new Set()); // Track which connectors are currently being fetched

  // Notification states
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationConnectorId, setNotificationConnectorId] = useState(null);
  const [notificationTransactionId, setNotificationTransactionId] = useState(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [notificationError, setNotificationError] = useState('');
  const [notificationSuccess, setNotificationSuccess] = useState(false);

  // Download-invoice-by-UPI-transaction-id states (for direct-UPI Paytm payments)
  const [upiInvoiceId, setUpiInvoiceId] = useState('');
  const [upiInvoiceError, setUpiInvoiceError] = useState('');
  const [upiInvoiceLoading, setUpiInvoiceLoading] = useState(false);

  // Get logo URL from domain config, fallback to default
  const logoUrl = domainConfig?.logoUrl || '/logo/amikCharge.svg';

  // Function to fetch charger details
  const fetchChargerDetails = async (chargerId) => {
    if (!chargerId) return;

    setChargerLoading(true);
    setChargerDetails(null); // Clear previous charger details
    try {
      const data = await getChargerDetails(chargerId);
      // Handle both response formats: {success: true, data: {...}} or direct object
      if (data) {
        if (data.success && data.data) {
          setChargerDetails(data.data);
        } else if (data.chargepoint_id || data.chargepoint_pk) {
          // Direct response object (as per sample response format)
          setChargerDetails(data);
        }
      }
    } catch (err) {
      // Don't set error for charger details
      setChargerDetails(null);
    } finally {
      setChargerLoading(false);
    }
  };

  // Function to fetch transaction for a specific connector
  const fetchTransactionForConnector = useCallback(async (chargerId, connectorId) => {
    try {
      // Convert to string and trim to handle both string and number connector IDs
      const chargerIdStr = String(chargerId || '').trim();
      const connectorIdStr = String(connectorId || '').trim();

      if (!chargerIdStr || !connectorIdStr) {
        return null;
      }

      const data = await getActiveTransaction(chargerIdStr, connectorIdStr);
      if (data && data.success && data.data) {
        return data.data;
      }
      return null;
    } catch (err) {
      return null;
    }
  }, []);

  // Read query parameters from URL on component mount - only chargerId
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlChargerId = searchParams.get('chargerId');

    const fetchAllData = async () => {
      if (!urlChargerId) {
        return; // Show charger ID input form instead
      }

      setLoading(true);
      setError('');
      setTransactions({});
      setStopError('');
      setHasSearched(true);
      setTransactionLoading({});
      // Reset refs when fetching new charger
      selectedConnectorIdRef.current = null;
      isInitialLoadRef.current = true;
      prevChargerDetailsRef.current = null;

      try {
        // Fetch charger details first
        await fetchChargerDetails(urlChargerId.trim());
      } catch (err) {
        setError(err.message || 'Failed to fetch charger details.');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [location.search]);

  // Fetch transaction for the first connector that is in use when charger details are loaded
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlChargerId = searchParams.get('chargerId');

    if (!urlChargerId || !chargerDetails || !chargerDetails.connectors || chargerDetails.connectors.length === 0) {
      return;
    }

    const fetchTransactionForFirstInUseConnector = async () => {
      // If charger status is N/A, don't make API calls
      const chargerStatus = chargerDetails.status?.toString().toUpperCase();
      if (chargerStatus === 'N/A' || chargerStatus === 'NA') {
        setTransactions({});
        return;
      }

      // Filter connectors that are in use
      const inUseConnectors = chargerDetails.connectors.filter((connector) => {
        const status = connector.status?.toLowerCase() || connector.connectorStatus?.toLowerCase() || '';
        const isInUse = status === 'in use' ||
          status === 'charging' ||
          status === 'preparing';
        return isInUse;
      });

      // If no connectors are in use, set empty transactions map
      if (inUseConnectors.length === 0) {
        setTransactions({});
        return;
      }

      // Get the first connector that is in use
      const firstInUseConnector = inUseConnectors[0];
      const connectorId = firstInUseConnector.connector_id || firstInUseConnector.connectorId || firstInUseConnector.id;

      if (!connectorId) {
        return;
      }

      // Skip if already fetching this connector
      if (fetchingRef.current.has(connectorId)) {
        return;
      }

      // Check if we already have the transaction using functional update to avoid stale state
      let hasTransaction = false;
      setTransactions(prev => {
        if (prev[connectorId]) {
          hasTransaction = true;
        }
        return prev;
      });

      if (hasTransaction) {
        return;
      }

      // Mark as fetching
      fetchingRef.current.add(connectorId);
      setTransactionLoading(prev => ({ ...prev, [connectorId]: true }));

      try {
        // Call public API: /cms/v1/public/transaction/active?chargerId={chargerId}&connectorId={connectorId}
        const transaction = await fetchTransactionForConnector(urlChargerId.trim(), connectorId);
        setTransactionLoading(prev => ({ ...prev, [connectorId]: false }));
        setTransactions(prev => {
          // Only update if we don't already have this transaction
          if (prev[connectorId]) {
            return prev;
          }
          return { ...prev, [connectorId]: transaction };
        });
        fetchingRef.current.delete(connectorId);
      } catch (err) {
        setTransactionLoading(prev => ({ ...prev, [connectorId]: false }));
        fetchingRef.current.delete(connectorId);
      }
    };

    fetchTransactionForFirstInUseConnector();
  }, [chargerDetails, location.search]);

  // Additional useEffect to fetch transactions for any connector that is in use but doesn't have a transaction
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlChargerId = searchParams.get('chargerId');

    if (!urlChargerId || !chargerDetails || !chargerDetails.connectors || chargerDetails.connectors.length === 0) {
      return;
    }

    // If charger status is N/A, don't make API calls
    const chargerStatus = chargerDetails.status?.toString().toUpperCase();
    if (chargerStatus === 'N/A' || chargerStatus === 'NA') {
      return;
    }

    // Find connectors that are in use but don't have transactions
    chargerDetails.connectors.forEach((connector) => {
      const status = connector.status?.toLowerCase() || connector.connectorStatus?.toLowerCase() || '';
      const isInUse = status === 'in use' ||
        status === 'charging' ||
        status === 'preparing';

      if (!isInUse) return;

      const connectorId = connector.connector_id || connector.connectorId || connector.id;
      if (!connectorId) return;

      // Check if we already have a transaction or are fetching (using functional updates to avoid stale state)
      let shouldFetch = true;
      setTransactions(prev => {
        if (prev[connectorId]) {
          shouldFetch = false;
        }
        return prev;
      });

      setTransactionLoading(prev => {
        if (prev[connectorId]) {
          shouldFetch = false;
        }
        return prev;
      });

      if (!shouldFetch || fetchingRef.current.has(connectorId)) {
        return;
      }

      // Mark as fetching
      fetchingRef.current.add(connectorId);
      setTransactionLoading(prev => ({ ...prev, [connectorId]: true }));

      fetchTransactionForConnector(urlChargerId.trim(), connectorId)
        .then((fetchedTransaction) => {
          setTransactionLoading(prev => ({ ...prev, [connectorId]: false }));
          if (fetchedTransaction) {
            setTransactions(prev => ({ ...prev, [connectorId]: fetchedTransaction }));
          }
          fetchingRef.current.delete(connectorId);
        })
        .catch((err) => {
          setTransactionLoading(prev => ({ ...prev, [connectorId]: false }));
          fetchingRef.current.delete(connectorId);
        });
    });
  }, [chargerDetails, location.search]);

  // Function to refresh all data — defined outside useEffect so it can be called manually
  const refreshData = useCallback(async () => {
    const searchParams = new URLSearchParams(location.search);
    const urlChargerId = searchParams.get('chargerId');

    if (!urlChargerId || !hasSearched) {
      return;
    }

    try {
      // Use isRefreshing (not chargerLoading) so the UI stays mounted during background refresh
      setIsRefreshing(true);
      const data = await getChargerDetails(urlChargerId.trim());
      let updatedChargerDetails = null;

      // Handle both response formats: {success: true, data: {...}} or direct object
      if (data) {
        if (data.success && data.data) {
          updatedChargerDetails = data.data;
        } else if (data.chargepoint_id || data.chargepoint_pk) {
          updatedChargerDetails = data;
        }
      }

      if (!updatedChargerDetails || !updatedChargerDetails.connectors) {
        setIsRefreshing(false);
        return;
      }

      // Update charger details in-place
      setChargerDetails(updatedChargerDetails);

      // Check charger status
      const chargerStatus = updatedChargerDetails.status?.toString().toUpperCase();
      if (chargerStatus === 'N/A' || chargerStatus === 'NA') {
        setTransactions({});
        setIsRefreshing(false);
        return;
      }

      // Refresh transactions for all connectors that are in use
      const refreshPromises = [];
      updatedChargerDetails.connectors.forEach((connector) => {
        const status = connector.status?.toLowerCase() || connector.connectorStatus?.toLowerCase() || '';
        const isInUse = status === 'in use' ||
          status === 'charging' ||
          status === 'preparing';

        if (!isInUse) return;

        const connectorId = connector.connector_id || connector.connectorId || connector.id;
        if (!connectorId || fetchingRef.current.has(connectorId)) return;

        fetchingRef.current.add(connectorId);

        const p = fetchTransactionForConnector(urlChargerId.trim(), connectorId)
          .then((fetchedTransaction) => {
            if (fetchedTransaction) {
              setTransactions(prev => ({ ...prev, [connectorId]: fetchedTransaction }));
            }
            fetchingRef.current.delete(connectorId);
          })
          .catch(() => {
            fetchingRef.current.delete(connectorId);
          });
        refreshPromises.push(p);
      });

      await Promise.allSettled(refreshPromises);
      setIsRefreshing(false);
    } catch (err) {
      setIsRefreshing(false);
      console.error('Refresh error:', err);
    }
  }, [location.search, hasSearched, fetchTransactionForConnector]);

  // Helper function to sort connectors the same way as in the render
  const getSortedConnectors = (connectors) => {
    if (!connectors || connectors.length === 0) return [];
    return [...connectors].sort((a, b) => {
      const aId = a.connector_id || a.connectorId || a.id || 0;
      const bId = b.connector_id || b.connectorId || b.id || 0;
      return Number(aId) - Number(bId);
    });
  };

  // Preserve tab selection when charger details are updated during auto-refresh
  useEffect(() => {
    if (!chargerDetails || !chargerDetails.connectors || chargerDetails.connectors.length === 0) {
      return;
    }

    // Get sorted connectors (same order as tabs are rendered)
    const sortedConnectors = getSortedConnectors(chargerDetails.connectors);

    // On initial load, just initialize the ref but don't change tab
    if (isInitialLoadRef.current) {
      // Use sorted connectors to match what's displayed in tabs
      const currentConnector = sortedConnectors[selectedTab];
      if (currentConnector) {
        const connectorId = currentConnector.connector_id || currentConnector.connectorId || currentConnector.id;
        if (connectorId) {
          selectedConnectorIdRef.current = connectorId;
        }
      }
      prevChargerDetailsRef.current = chargerDetails;
      isInitialLoadRef.current = false;
      return; // Don't change tab on initial load
    }

    // On auto-refresh (chargerDetails changed), preserve tab by connector ID
    // Only run preservation logic if we have a tracked connector ID
    if (selectedConnectorIdRef.current) {
      // Find connector in sorted array (same order as tabs)
      const connectorIndex = sortedConnectors.findIndex((connector) => {
        const connectorId = connector.connector_id || connector.connectorId || connector.id;
        return connectorId === selectedConnectorIdRef.current;
      });

      if (connectorIndex !== -1) {
        // Found the previously selected connector - preserve that tab
        // Use functional update to ensure we're working with latest state
        setSelectedTab(prevTab => {
          // Only change if different - this prevents unnecessary updates
          if (prevTab !== connectorIndex) {
            return connectorIndex;
          }
          return prevTab; // Keep current tab if it's already correct
        });
        prevChargerDetailsRef.current = chargerDetails;
        return;
      }
      // If connector not found, fall through to bounds check
    }

    // Update previous charger details ref
    prevChargerDetailsRef.current = chargerDetails;

    // Fallback: Only adjust if current tab is out of bounds - otherwise preserve current selection
    const maxTab = sortedConnectors.length - 1;
    setSelectedTab(prevTab => {
      if (prevTab > maxTab) {
        // Only adjust if out of bounds
        return Math.max(0, maxTab);
      }
      // Keep the current tab selection - don't change it
      return prevTab;
    });
  }, [chargerDetails]);

  // Update the selected connector ID ref when tab changes (either manually or programmatically)
  // This runs AFTER tab changes to keep ref in sync
  useEffect(() => {
    if (!chargerDetails || !chargerDetails.connectors || chargerDetails.connectors.length === 0) {
      return;
    }

    // Get sorted connectors (same order as tabs are rendered)
    const sortedConnectors = getSortedConnectors(chargerDetails.connectors);

    // Ensure selectedTab is within bounds
    const maxTab = sortedConnectors.length - 1;
    const validTab = Math.min(selectedTab, maxTab);

    // Use sorted connectors to match what's displayed in tabs
    const currentConnector = sortedConnectors[validTab];
    if (currentConnector) {
      const connectorId = currentConnector.connector_id || currentConnector.connectorId || currentConnector.id;
      if (connectorId && selectedConnectorIdRef.current !== connectorId) {
        // Only update ref if it's different to avoid unnecessary updates
        selectedConnectorIdRef.current = connectorId;
      }
    }
  }, [selectedTab, chargerDetails]);

  const getCurrentTransaction = () => {
    if (!chargerDetails || !chargerDetails.connectors || chargerDetails.connectors.length === 0) {
      return null;
    }
    const currentConnector = chargerDetails.connectors[selectedTab];
    if (!currentConnector) return null;
    const connectorId = currentConnector.connector_id || currentConnector.connectorId || currentConnector.id;
    return connectorId ? transactions[connectorId] || null : null;
  };

  const handleChargerSearch = () => {
    const value = chargerIdInput.trim();
    if (!value) {
      setChargerIdInputError('Please enter a charger ID');
      return;
    }
    setChargerIdInputError('');
    navigate(`/active-transaction?chargerId=${encodeURIComponent(value)}`);
  };

  const handleStopClick = (connectorId = null) => {
    // Get the connector ID - use provided one or get from current tab
    let targetConnectorId = connectorId;
    if (!targetConnectorId) {
      const currentConnector = chargerDetails?.connectors?.[selectedTab];
      targetConnectorId = currentConnector?.connector_id || currentConnector?.connectorId || currentConnector?.id;
    }

    // Store the connector ID
    setStopConnectorId(targetConnectorId);

    // Get transaction from that specific connector's transaction details section
    // Each connector has its own transaction stored in transactions[connectorId]
    const transactionToStop = targetConnectorId ? transactions[targetConnectorId] || null : null;

    // Get transaction ID from that specific connector's transaction details section
    // This transaction ID is from the transaction.transactionId field in that connector's transaction details
    const transactionId = transactionToStop?.transactionId || null;
    setStopTransactionId(transactionId);

    // Always open the dialog when button is clicked
    setStopDialogOpen(true);
    setStopError('');
    setUtrNumber('');
    setUtrNumberError('');
  };

  const handleStopCancel = () => {
    setStopDialogOpen(false);
    setStopError('');
    setStopPhoneNumber('');
    setStopPhoneError('');
    setStopOtpSent(false);
    setStopOtp('');
    setStopOtpError('');
    setUtrNumber('');
    setUtrNumberError('');
    setStopConnectorId(null);
    setStopTransactionId(null);
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phone || phone.trim() === '') return 'Phone number is required';
    if (!phoneRegex.test(phone.trim())) return 'Please enter a valid 10-digit mobile number';
    return '';
  };

  const handleStopPhoneChange = (event) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 10);
    setStopPhoneNumber(value);
    if (stopPhoneError) {
      setStopPhoneError(validatePhoneNumber(value));
    }
  };

  const handleStopSendOTP = async () => {
    const error = validatePhoneNumber(stopPhoneNumber);
    if (error) { setStopPhoneError(error); return; }
    setStopPhoneError('');
    setSendingStopOtp(true);
    setStopError('');
    try {
      const searchParams = new URLSearchParams(location.search);
      const urlChargerId = searchParams.get('chargerId');
      const domain = domainConfig?.domain || 'amiktechnologies';
      const data = await sendStopOTP(urlChargerId?.trim(), String(stopConnectorId), stopPhoneNumber.trim(), '+91', domain);
      if (data && data.success) {
        setStopOtpSent(true);
      } else {
        setStopError(data?.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setStopError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setSendingStopOtp(false);
    }
  };

  const handleStopOtpChange = (event) => {
    const value = event.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setStopOtp(value);
      if (stopOtpError && value.length === 6) setStopOtpError('');
    }
  };

  const handleStopVerifyAndStop = async () => {
    if (!stopOtp || stopOtp.length !== 6) {
      setStopOtpError('Please enter a valid 6-digit OTP');
      return;
    }
    setStopOtpError('');
    setVerifyingStopOtp(true);
    setStopError('');

    let transactionId = stopTransactionId;
    if (!transactionId && stopConnectorId) {
      transactionId = transactions[stopConnectorId]?.transactionId;
    }
    if (!transactionId) {
      setStopError('Transaction ID is missing.');
      setVerifyingStopOtp(false);
      return;
    }

    try {
      const domain = domainConfig?.domain || 'amiktechnologies';
      const data = await verifyStopOTP(stopPhoneNumber.trim(), '+91', stopOtp, transactionId, domain);

      if (data && data.success) {
        // OTP verified and remote stop triggered — refresh transaction
        const searchParams = new URLSearchParams(location.search);
        const urlChargerId = searchParams.get('chargerId');
        const connectorIdToRefresh = stopConnectorId || (chargerDetails?.connectors?.[selectedTab]?.connector_id || chargerDetails?.connectors?.[selectedTab]?.connectorId || chargerDetails?.connectors?.[selectedTab]?.id);
        if (urlChargerId && connectorIdToRefresh) {
          try {
            const refreshData = await getActiveTransaction(urlChargerId.trim(), connectorIdToRefresh.trim());
            if (refreshData && refreshData.success && refreshData.data) {
              setTransactions(prev => ({ ...prev, [connectorIdToRefresh]: refreshData.data }));
            } else {
              setTransactions(prev => ({ ...prev, [connectorIdToRefresh]: null }));
            }
          } catch {
            setTransactions(prev => ({ ...prev, [connectorIdToRefresh]: null }));
          }
        }
        setStopDialogOpen(false);
        setStopConnectorId(null);
        setStopTransactionId(null);
        setError('');
      } else {
        setStopError(data?.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setStopError(err.message || 'Failed to verify OTP. Please try again.');
    } finally {
      setVerifyingStopOtp(false);
    }
  };

  // Notification handlers
  const handleNotificationClick = (connectorId, transactionId) => {
    setNotificationConnectorId(connectorId);
    setNotificationTransactionId(transactionId);
    setNotificationDialogOpen(true);
    setEmail('');
    setEmailError('');
    setOtpSent(false);
    setOtp('');
    setOtpError('');
    setNotificationError('');
    setNotificationSuccess(false);
  };

  const handleNotificationClose = () => {
    setNotificationDialogOpen(false);
    setNotificationConnectorId(null);
    setNotificationTransactionId(null);
    setEmail('');
    setEmailError('');
    setOtpSent(false);
    setOtp('');
    setOtpError('');
    setNotificationError('');
    setNotificationSuccess(false);
  };

  const validateEmail = (emailValue) => {
    // Email regex pattern
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailValue || emailValue.trim() === '') {
      return 'Email is required';
    }

    if (!emailRegex.test(emailValue.trim())) {
      return 'Please enter a valid email address (e.g., user@example.com)';
    }

    // Check length
    if (emailValue.length > 100) {
      return 'Email address should be 100 characters or less';
    }

    return '';
  };


  const handleEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);
    if (emailError) {
      const error = validateEmail(value);
      setEmailError(error);
    }
  };

  const handleSendOTP = async () => {
    // Validate email
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }
    setEmailError('');
    const contactValue = email.trim();

    setSendingOtp(true);
    setNotificationError('');

    try {
      // Send OTP to email
      const data = await sendOTPToEmail(contactValue, notificationTransactionId);

      if (data && data.success) {
        setOtpSent(true);
        setNotificationError('');
      } else {
        setNotificationError(data?.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setNotificationError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleOTPChange = (event) => {
    const value = event.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setOtp(value);
      if (otpError && value.length === 6) {
        setOtpError('');
      }
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }

    setOtpError('');
    setVerifyingOtp(true);
    setNotificationError('');

    try {
      if (!notificationTransactionId) {
        setNotificationError('Transaction ID is missing.');
        setVerifyingOtp(false);
        return;
      }

      const contactValue = email.trim();

      const data = await verifyOTP(contactValue, otp, notificationTransactionId);

      if (data && data.success) {
        setNotificationSuccess(true);
        setNotificationError('');
        // Close dialog after 2 seconds
        setTimeout(() => {
          handleNotificationClose();
        }, 2000);
      } else {
        setNotificationError(data?.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setNotificationError(err.message || 'Failed to verify OTP. Please try again.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Helper function to render connector content (transaction or status)
  const renderConnectorContent = (connector, connectorId, isLoading) => {
    const transaction = transactions[connectorId];

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
          <CircularProgress size={40} />
        </Box>
      );
    }

    // Check if connector is in use
    const isInUse = connector.status?.toLowerCase() === 'in use' ||
      connector.connectorStatus?.toLowerCase() === 'in use' ||
      connector.status?.toLowerCase() === 'charging' ||
      connector.status?.toLowerCase() === 'preparing' ||
      connector.connectorStatus?.toLowerCase() === 'charging' ||
      connector.connectorStatus?.toLowerCase() === 'preparing';

    // If connector is in use, show transaction details (or loading state if transaction is being fetched)
    if (isInUse) {
      // If transaction is still loading, show loading state
      if (isLoading) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
            <CircularProgress size={40} />
          </Box>
        );
      }

      // If transaction exists, show transaction details
      if (transaction) {
        return (
          <>
            {/* Connector Information */}
            <DetailCard sx={{ mb: 2, boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}` }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: { xs: 32, sm: 36 },
                      height: { xs: 32, sm: 36 },
                      borderRadius: 1.5,
                      background: alpha(theme.palette.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon
                      icon={flashFill}
                      width={18}
                      height={18}
                      style={{ color: theme.palette.primary.main }}
                    />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: (connectorId === '1' || connectorId === 1)
                        ? '#0d47a1'
                        : (connectorId === '2' || connectorId === 2)
                          ? '#1b5e20'
                          : 'text.primary',
                      fontSize: { xs: '1rem', sm: '1.125rem' },
                    }}
                  >
                    Connector {connectorId} Information
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 1.5, borderColor: alpha(theme.palette.primary.main, 0.1) }} />
                <Grid container spacing={{ xs: 1, sm: 1.25 }}>
                  {connector.connectorType && (
                    <Grid item xs={6} sm={6} md={4}>
                      <GridLabel>
                        <Typography className="label" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Connector Type</Typography>
                        <Typography className="value" sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                          {getConnectorTypeName(connector.connectorType)}
                        </Typography>
                      </GridLabel>
                    </Grid>
                  )}
                  {connector.kw && (
                    <Grid item xs={6} sm={6} md={4}>
                      <GridLabel>
                        <Typography className="label" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Power</Typography>
                        <Typography className="value" sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                          {connector.kw} kW
                        </Typography>
                      </GridLabel>
                    </Grid>
                  )}
                  {connector.priceperKW && (
                    <Grid item xs={6} sm={6} md={4}>
                      <GridLabel>
                        <Typography className="label" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Price</Typography>
                        <Typography className="value" sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                          ₹{connector.priceperKW}/{connector.priceType || 'kWh'} + GST
                        </Typography>
                      </GridLabel>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </DetailCard>

            {/* Transaction Details */}
            <DetailCard>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: { xs: 32, sm: 36 },
                        height: { xs: 32, sm: 36 },
                        borderRadius: 1.5,
                        background: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon
                        icon={fileTextFill}
                        width={18}
                        height={18}
                        style={{ color: theme.palette.primary.main }}
                      />
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: { xs: '1rem', sm: '1.125rem' },
                      }}
                    >
                      Transaction Details
                    </Typography>
                  </Stack>
                  <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, justifyContent: { xs: 'center', sm: 'flex-end' } }}>
                    {transaction.isActive && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="medium"
                        onClick={() => handleNotificationClick(connectorId, transaction.transactionId)}
                        startIcon={<Icon icon={bellFill} width={18} height={18} />}
                        sx={{
                          width: { xs: '100%', sm: 'auto' },
                          fontWeight: 700,
                          fontSize: { xs: '0.9375rem', sm: '1rem' },
                          px: { xs: 2, sm: 3 },
                          py: { xs: 1, sm: 1.25 },
                          borderRadius: 2,
                          minWidth: { xs: 'auto', sm: 200 },
                          border: '2px solid',
                          '&:hover': {
                            border: '2px solid',
                            transform: { xs: 'none', sm: 'translateY(-2px)' },
                          },
                        }}
                      >
                        Get Charging Updates
                      </Button>
                    )}
                    {transaction.isActive ? (
                      <Button
                        variant="contained"
                        color="error"
                        size="medium"
                        onClick={() => handleStopClick(connectorId)}
                        disabled={verifyingStopOtp}
                        startIcon={<Icon icon={stopCircleFill} width={18} height={18} />}
                        sx={{
                          width: { xs: '100%', sm: 'auto' },
                          fontWeight: 700,
                          fontSize: { xs: '0.9375rem', sm: '1rem' },
                          px: { xs: 2, sm: 3 },
                          py: { xs: 1, sm: 1.25 },
                          borderRadius: 2,
                          minWidth: { xs: 'auto', sm: 180 },
                          boxShadow: '0 4px 16px rgba(211, 47, 47, 0.4)',
                          backgroundColor: '#d32f2f',
                          border: '1px solid #b71c1c',
                          '&:hover': {
                            boxShadow: '0 6px 20px rgba(211, 47, 47, 0.5)',
                            transform: { xs: 'none', sm: 'translateY(-2px) scale(1.02)' },
                            backgroundColor: '#b71c1c',
                            border: '2px solid #8b0000',
                          },
                          '&:active': {
                            transform: 'translateY(0) scale(0.98)',
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:disabled': {
                            backgroundColor: '#ffcdd2',
                            color: '#ffffff',
                            border: '2px solid #ffcdd2',
                          },
                        }}
                      >
                        <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                          Stop Transaction
                          <br />
                          <span style={{ fontSize: '0.9em', fontWeight: 600, opacity: 0.95 }}>Connector {connectorId}</span>
                        </span>
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        onClick={() => handleDownloadInvoice()}
                        startIcon={<Icon icon={downloadFill} width={18} height={18} />}
                        sx={{
                          width: { xs: '100%', sm: 'auto' },
                          fontWeight: 700,
                          fontSize: { xs: '0.9375rem', sm: '1rem' },
                          px: { xs: 2, sm: 3 },
                          py: { xs: 1, sm: 1.25 },
                          borderRadius: 2,
                          minWidth: { xs: 'auto', sm: 180 },
                          boxShadow: '0 4px 16px rgba(25, 118, 210, 0.4)',
                          backgroundColor: '#1976d2',
                          border: '1px solid #1565c0',
                          '&:hover': {
                            boxShadow: '0 6px 20px rgba(25, 118, 210, 0.5)',
                            transform: { xs: 'none', sm: 'translateY(-2px) scale(1.02)' },
                            backgroundColor: '#1565c0',
                            border: '2px solid #0d47a1',
                          },
                          '&:active': {
                            transform: 'translateY(0) scale(0.98)',
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        Download Invoice
                      </Button>
                    )}
                  </Box>
                </Stack>
                <Divider sx={{ mb: { xs: 1, sm: 1.5 }, borderColor: alpha(theme.palette.primary.main, 0.1) }} />

                <Grid container spacing={{ xs: 1, sm: 1.25 }}>
                  <Grid item xs={6} sm={6} md={4}>
                    <GridLabel>
                      <Typography className="label" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Transaction ID</Typography>
                      <Typography className="value" sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>{formatValue(transaction.transactionId)}</Typography>
                    </GridLabel>
                  </Grid>
                  <Grid item xs={6} sm={6} md={4}>
                    <GridLabel>
                      <Typography className="label" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Start Timestamp</Typography>
                      <Typography className="value" sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                        {transaction.startTimestamp ? fDateTime(transaction.startTimestamp) : '—'}
                      </Typography>
                    </GridLabel>
                  </Grid>
                  <Grid item xs={6} sm={6} md={4}>
                    <GridLabel>
                      <Typography className="label" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>kW Consumed</Typography>
                      <Typography className="value" sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>{formatValue(transaction.kwConsumed)}</Typography>
                    </GridLabel>
                  </Grid>
                  {(transaction.amountPaid !== undefined && transaction.amountPaid !== null) && (
                    <Grid item xs={6} sm={6} md={4}>
                      <GridLabel>
                        <Typography className="label" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Amount Paid</Typography>
                        <Typography className="value" sx={{ fontWeight: 600, color: 'success.main', fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                          ₹{formatValue(transaction.amountPaid)}
                        </Typography>
                      </GridLabel>
                    </Grid>
                  )}
                  {(transaction.amountPaid !== undefined && transaction.amountPaid !== null) && (
                    <Grid item xs={6} sm={6} md={4}>
                      <GridLabel>
                        <Typography className="label" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Amount Used (Incl. GST) </Typography>
                        <Typography className="value" sx={{ fontWeight: 600, color: 'warning.main', fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                          ₹{formatValue(calculateAmountUsed(transaction, connector))}
                        </Typography>
                      </GridLabel>
                    </Grid>
                  )}
                  {(transaction.amountPaid !== undefined && transaction.amountPaid !== null) && (
                    <Grid item xs={6} sm={6} md={4}>
                      <GridLabel>
                        <Typography className="label" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Amount Remaining</Typography>
                        <Typography className="value" sx={{
                          fontWeight: 600, color: (() => {
                            const remaining = calculateAmountRemaining(transaction, connector);
                            if (remaining === null) return 'text.secondary';
                            return parseFloat(remaining) >= 0 ? 'info.main' : 'error.main';
                          })(), fontSize: { xs: '0.9375rem', sm: '1rem' }
                        }}>
                          ₹{formatValue(calculateAmountRemaining(transaction, connector))}
                        </Typography>
                      </GridLabel>
                    </Grid>
                  )}
                  <Grid item xs={6} sm={6} md={4}>
                    <GridLabel>
                      <Typography className="label" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Battery Percent</Typography>
                      <Typography className="value" sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>{formatValue(transaction.batteryPercent)}%</Typography>
                    </GridLabel>
                  </Grid>
                </Grid>
              </CardContent>
            </DetailCard>
          </>
        );
      }

      // If connector is in use but no transaction found, show loading or message
      // The useEffect will handle fetching the transaction
      if (transactionLoading[connectorId]) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
            <CircularProgress size={40} />
          </Box>
        );
      }

      // If still no transaction after API call, show message
      return (
        <DetailCard sx={{
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          width: '100%',
          maxWidth: '100%',
        }}>
          <CardContent sx={{
            p: { xs: 1.5, sm: 2 },
            width: '100%',
            '&:last-child': { pb: { xs: 1.5, sm: 2 } }
          }}>
            <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.9375rem', sm: '1rem' }, lineHeight: 1.5 }}>
                Connector {connectorId} is in use, but no active transaction was found. The transaction may have been completed or is being processed.
              </Typography>
            </Alert>
          </CardContent>
        </DetailCard>
      );
    } else {
      // Show Connector Status (when connector is not in use)
      return (
        <DetailCard sx={{
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          width: '100%',
          maxWidth: '100%',
        }}>
          <CardContent sx={{
            p: { xs: 1.5, sm: 2 },
            width: '100%',
            '&:last-child': { pb: { xs: 1.5, sm: 2 } }
          }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  borderRadius: 1.5,
                  background: alpha(theme.palette.primary.main, 0.15),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <Icon
                  icon={flashFill}
                  width={20}
                  height={20}
                  style={{ color: theme.palette.primary.main }}
                />
              </Box>
              <Box sx={{ flex: 1, textAlign: 'left' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: (connectorId === '1' || connectorId === 1)
                      ? '#0d47a1'
                      : (connectorId === '2' || connectorId === 2)
                        ? '#1b5e20'
                        : 'text.primary',
                    fontSize: { xs: '1.05rem', sm: '1.25rem' },
                    textAlign: 'left',
                    letterSpacing: '0.2px',
                    lineHeight: 1.3,
                  }}
                >
                  Connector {connectorId} Status
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                  }}
                >
                  Current status and details
                </Typography>
              </Box>
            </Stack>

            <Box
              sx={{
                width: '100%',
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                background: alpha(theme.palette.primary.main, 0.06),
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                },
              }}
            >
              <Stack direction="column" spacing={1.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.75, sm: 1 }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                  <Box sx={{ flex: 1, textAlign: 'left' }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: (connectorId === '1' || connectorId === 1)
                          ? '#0d47a1'
                          : (connectorId === '2' || connectorId === 2)
                            ? '#1b5e20'
                            : 'text.secondary',
                        fontSize: { xs: '0.95rem', sm: '1.125rem' },
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.6px',
                        display: 'block',
                        lineHeight: 1.3,
                      }}
                    >
                      Connector {connectorId}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={
                        (chargerDetails.status?.toString().toUpperCase() === 'N/A' || chargerDetails.status?.toString().toUpperCase() === 'NA')
                          ? 'N/A'
                          : chargerDetails.status !== 'A' && chargerDetails.status?.toLowerCase() !== 'active'
                            ? 'Inactive'
                            : connector.status || connector.connectorStatus || 'Unknown'
                      }
                      size="small"
                      color={
                        (chargerDetails.status?.toString().toUpperCase() === 'N/A' || chargerDetails.status?.toString().toUpperCase() === 'NA')
                          ? 'default'
                          : chargerDetails.status !== 'A' && chargerDetails.status?.toLowerCase() !== 'active'
                            ? 'error'
                            : connector.status?.toLowerCase() === 'available' ||
                              connector.connectorStatus?.toLowerCase() === 'available'
                              ? 'success'
                              : connector.status?.toLowerCase() === 'in use' ||
                                connector.connectorStatus?.toLowerCase() === 'in use' ||
                                connector.status?.toLowerCase() === 'charging' ||
                                connector.status?.toLowerCase() === 'preparing' ||
                                connector.connectorStatus?.toLowerCase() === 'charging' ||
                                connector.connectorStatus?.toLowerCase() === 'preparing'
                                ? 'warning'
                                : connector.status?.toLowerCase() === 'unavailable' ||
                                  connector.status?.toLowerCase() === 'not available' ||
                                  connector.status?.toLowerCase() === 'faulted' ||
                                  connector.connectorStatus?.toLowerCase() === 'unavailable' ||
                                  connector.connectorStatus?.toLowerCase() === 'not available' ||
                                  connector.connectorStatus?.toLowerCase() === 'faulted'
                                  ? 'error'
                                  : 'default'
                      }
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                        height: { xs: 26, sm: 28 },
                        minWidth: { xs: 80, sm: 90 },
                      }}
                    />
                    {isInUse ? (
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleStopClick(connectorId)}
                        disabled={verifyingStopOtp}
                        startIcon={<Icon icon={stopCircleFill} width={16} height={16} />}
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                          px: { xs: 1.25, sm: 1.5 },
                          py: { xs: 0.5, sm: 0.75 },
                          borderRadius: 1.5,
                          minWidth: 'auto',
                          height: { xs: 32, sm: 34 },
                          boxShadow: '0 2px 8px rgba(211, 47, 47, 0.3)',
                          backgroundColor: '#d32f2f',
                          border: '1px solid #b71c1c',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(211, 47, 47, 0.4)',
                            backgroundColor: '#b71c1c',
                          },
                          '&:disabled': {
                            backgroundColor: '#ffcdd2',
                            color: '#ffffff',
                          },
                        }}
                      >
                        <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                          Stop Transaction
                          <br />
                          <span style={{ fontSize: '0.9em', fontWeight: 600, opacity: 0.95 }}>Connector {connectorId}</span>
                        </span>
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
                <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.15) }} />
                <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ width: '100%' }}>
                  {connector.connectorType && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box
                        sx={{
                          p: { xs: 1, sm: 1.25 },
                          borderRadius: 2,
                          background: theme.palette.background.paper,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          height: '100%',
                          minHeight: { xs: 80, sm: 90 },
                          width: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'block',
                            mb: 0.5,
                          }}
                        >
                          Type
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.primary',
                            fontSize: { xs: '0.9375rem', sm: '1rem' },
                            fontWeight: 600,
                          }}
                        >
                          {getConnectorTypeName(connector.connectorType)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {connector.kw && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box
                        sx={{
                          p: { xs: 1, sm: 1.25 },
                          borderRadius: 2,
                          background: theme.palette.background.paper,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          height: '100%',
                          minHeight: { xs: 80, sm: 90 },
                          width: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'block',
                            mb: 0.5,
                          }}
                        >
                          Power
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.primary',
                            fontSize: { xs: '0.9375rem', sm: '1rem' },
                            fontWeight: 600,
                          }}
                        >
                          {connector.kw} kW
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {connector.priceperKW && (
                    <Grid item xs={12} sm={12} md={4}>
                      <Box
                        sx={{
                          p: { xs: 1, sm: 1.25 },
                          borderRadius: 2,
                          background: theme.palette.background.paper,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          height: '100%',
                          minHeight: { xs: 80, sm: 90 },
                          width: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'block',
                            mb: 0.5,
                          }}
                        >
                          Price
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.primary',
                            fontSize: { xs: '0.9375rem', sm: '1rem' },
                            fontWeight: 600,
                          }}
                        >
                          ₹{connector.priceperKW}/{connector.priceType || 'kWh'} + GST
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Stack>
            </Box>
          </CardContent>
        </DetailCard>
      );
    }
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return '—';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  };

  // Calculate price per kW with GST
  const getPricePerKwWithGST = (transaction, connector = null) => {
    if (!transaction) return null;
    const pricePerKw = parseFloat(transaction.pricePerKw || (connector && connector.priceperKW) || 0);
    const taxPercent = parseFloat(transaction?.taxPercent || connector?.taxPercent || 18);
    if (pricePerKw > 0) {
      return (pricePerKw * (1 + (taxPercent / 100))).toFixed(2);
    }
    return null;
  };

  // Calculate base amount (before GST)
  const calculateBaseAmount = (transaction, connector = null) => {
    if (!transaction) return null;
    // Try to get price from transaction first, then from connector
    const pricePerKw = parseFloat(
      transaction.pricePerKw ||
      (connector && connector.priceperKW) ||
      0
    );
    const kwConsumed = parseFloat(transaction.kwConsumed || 0);
    if (pricePerKw > 0 && kwConsumed > 0) {
      return (pricePerKw * kwConsumed).toFixed(2);
    }
    return null;
  };

  // Calculate GST amount
  const calculateGSTAmount = (transaction, connector = null) => {
    const baseAmount = calculateBaseAmount(transaction, connector);
    const taxPercent = parseFloat(transaction?.taxPercent || connector?.taxPercent || 18);
    if (baseAmount !== null) {
      return (parseFloat(baseAmount) * (taxPercent / 100)).toFixed(2);
    }
    return null;
  };

  // Calculate total amount used with GST
  const calculateAmountUsed = (transaction, connector = null) => {
    if (!transaction) return null;
    // Try to get price from transaction first, then from connector
    const pricePerKw = parseFloat(
      transaction.pricePerKw ||
      (connector && connector.priceperKW) ||
      0
    );
    const kwConsumed = parseFloat(transaction.kwConsumed || 0);
    const taxPercent = parseFloat(transaction?.taxPercent || connector?.taxPercent || 18);
    if (pricePerKw > 0 && kwConsumed > 0) {
      // Calculate: (pricePerKw * (1 + taxPercent/100)) * kwConsumed (price with GST * consumption)
      const calculated = pricePerKw * (1 + (taxPercent / 100)) * kwConsumed;
      // Cap minimum amount used at 0.00
      return Math.max(0.00, calculated).toFixed(2);
    }
    return null;
  };

  const calculateAmountRemaining = (transaction, connector = null) => {
    if (!transaction) return null;
    const amountPaid = parseFloat(transaction.amountPaid || 0);
    const amountUsed = calculateAmountUsed(transaction, connector);
    if (amountPaid > 0 && amountUsed !== null) {
      const remaining = amountPaid - parseFloat(amountUsed);
      // Cap minimum amount remaining at 0.00 (prevent negative values)
      return Math.max(0.00, remaining).toFixed(2);
    }
    return null;
  };

  // Generate and download invoice PDF.
  // With no args it uses the currently-selected charger transaction/connector.
  // A transaction (and optional connector) can be passed in — e.g. the
  // download-by-UPI-transaction-id flow, which passes a connector of null so the
  // invoice reads price/tax straight off the transaction fields.
  const handleDownloadInvoice = (transactionArg = null, connectorArg = null) => {
    const transaction = transactionArg || getCurrentTransaction();
    if (!transaction) return;

    // Get the current connector for price information
    const currentConnector = transactionArg
      ? connectorArg
      : (chargerDetails?.connectors?.[selectedTab] || null);

    const startTime = transaction.startTimestamp ? fDateTime(transaction.startTimestamp) : 'N/A';
    const stopTime = transaction.stopTimestamp ? fDateTime(transaction.stopTimestamp) : 'N/A';
    const kwConsumed = parseFloat(transaction.kwConsumed || 0);
    const baseAmount = calculateBaseAmount(transaction, currentConnector);
    const gstAmount = calculateGSTAmount(transaction, currentConnector);
    const totalAmount = calculateAmountUsed(transaction, currentConnector);
    const amountPaid = parseFloat(transaction.amountPaid || 0);
    const amountRemaining = calculateAmountRemaining(transaction, currentConnector);

    // Construct full logo URL from domain config
    const domainLogoUrl = domainConfig?.logoUrl || logoUrl;
    const fullLogoUrl = domainLogoUrl.startsWith('http')
      ? domainLogoUrl
      : `${window.location.origin}${domainLogoUrl.startsWith('/') ? domainLogoUrl : '/' + domainLogoUrl}`;

    // Fallback logo URL
    const fallbackLogoUrl = `${window.location.origin}/logo/amikCharge.svg`;

    // ---- Invoice display helpers ----
    const taxPct = parseFloat(transaction?.taxPercent || currentConnector?.taxPercent || 18);
    const rateDisplay = transaction.pricePerKw
      ? `₹${parseFloat(transaction.pricePerKw).toFixed(2)}`
      : '—';
    const invoiceDateDisplay =
      stopTime !== 'N/A' ? stopTime : (startTime !== 'N/A' ? startTime : fDateTime(new Date()));
    const statusLabel = transaction.isActive ? 'IN PROGRESS' : 'PAID';
    const statusClass = transaction.isActive ? 'status-active' : 'status-paid';
    const isUpi = Boolean(transaction.upiTransactionId);
    const paymentRef = transaction.upiTransactionId || null;
    const orderRef = transaction.orderId || null;
    const companyName = domainConfig?.name || 'Amik Charge';
    const supportEmail = domainConfig?.email || '';

    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Invoice ${transaction.transactionId}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            color: #1f2937;
            background: #ffffff;
            font-size: 12px;
            line-height: 1.5;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .invoice-container {
            width: 210mm;
            min-height: 297mm;
            margin: 0 auto;
            background: #ffffff;
            padding: 16mm 14mm 12mm;
            display: flex;
            flex-direction: column;
          }
          /* Header */
          .inv-head {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 16px;
          }
          .brand { display: flex; align-items: center; gap: 12px; }
          .brand-logo {
            width: 64px;
            height: 64px;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 8px;
            background: #ffffff;
            flex-shrink: 0;
          }
          .brand-logo img {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
            object-fit: contain;
            display: block;
          }
          .brand-name { font-size: 18px; font-weight: 700; color: #0d47a1; letter-spacing: .2px; }
          .brand-sub { font-size: 11px; color: #6b7280; margin-top: 2px; }
          .inv-title { text-align: right; }
          .inv-title h1 { font-size: 26px; font-weight: 800; letter-spacing: 3px; color: #0f172a; }
          .inv-meta { margin-top: 10px; font-size: 11.5px; color: #374151; }
          .inv-meta div { margin-top: 4px; }
          .inv-meta .k { color: #6b7280; display: inline-block; min-width: 74px; }
          .inv-meta .v { font-weight: 600; color: #111827; }
          .badge {
            display: inline-block;
            padding: 3px 10px;
            border-radius: 999px;
            font-size: 10px;
            font-weight: 700;
            letter-spacing: .5px;
          }
          .status-paid { background: #dcfce7; color: #15803d; }
          .status-active { background: #fef3c7; color: #b45309; }
          .rule {
            height: 3px;
            background: linear-gradient(90deg, #0d47a1, #1a73e8 55%, #34a853);
            border-radius: 2px;
            margin: 16px 0 20px;
          }
          /* Info cards */
          .cards { display: flex; gap: 14px; margin-bottom: 20px; }
          .card {
            flex: 1;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 14px;
            background: #f8fafc;
          }
          .card h3 {
            font-size: 10.5px;
            text-transform: uppercase;
            letter-spacing: .6px;
            color: #6b7280;
            margin-bottom: 8px;
            font-weight: 700;
          }
          .row { display: flex; justify-content: space-between; gap: 10px; padding: 3px 0; font-size: 11.5px; }
          .row .lbl { color: #6b7280; }
          .row .val { font-weight: 600; color: #111827; text-align: right; word-break: break-word; }
          /* Items table */
          table.items { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
          table.items thead th {
            background: #0f172a;
            color: #ffffff;
            font-size: 10.5px;
            text-transform: uppercase;
            letter-spacing: .5px;
            padding: 10px 12px;
            text-align: left;
          }
          table.items thead th.num { text-align: right; }
          table.items tbody td {
            padding: 11px 12px;
            border-bottom: 1px solid #eef2f7;
            font-size: 12px;
            vertical-align: top;
          }
          table.items tbody td.num { text-align: right; white-space: nowrap; }
          .item-desc { font-weight: 600; color: #111827; }
          .item-sub { color: #6b7280; font-size: 11px; margin-top: 2px; }
          /* Totals */
          .totals { display: flex; justify-content: flex-end; }
          .totals-box { width: 280px; }
          .totals-box .row { padding: 5px 0; font-size: 12px; }
          .totals-box .row.total {
            border-top: 2px solid #0f172a;
            margin-top: 6px;
            padding-top: 9px;
            font-size: 14px;
          }
          .totals-box .row.total .lbl { color: #111827; font-weight: 700; }
          .totals-box .row.total .val { color: #0d47a1; font-weight: 800; }
          .totals-box .row.paid .val { color: #15803d; }
          .totals-box .row.refund .val { color: #b45309; }
          /* Payment */
          .pay {
            margin-top: 18px;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 14px;
            background: #f8fafc;
          }
          .pay h3 {
            font-size: 10.5px;
            text-transform: uppercase;
            letter-spacing: .6px;
            color: #6b7280;
            margin-bottom: 8px;
            font-weight: 700;
          }
          .pay-grid { display: flex; flex-wrap: wrap; gap: 6px 28px; }
          .pay-item { font-size: 11.5px; }
          .pay-item .lbl { color: #6b7280; }
          .pay-item .val { font-weight: 600; color: #111827; }
          /* Footer */
          .foot { margin-top: auto; padding-top: 20px; text-align: center; color: #6b7280; font-size: 10.5px; }
          .foot strong { color: #374151; }
          .foot .thanks { font-size: 12px; color: #0d47a1; font-weight: 700; margin-bottom: 4px; }
          .foot .divider { height: 1px; background: #e5e7eb; margin: 0 auto 12px; max-width: 80%; }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="inv-head">
            <div class="brand">
              <div class="brand-logo">
                <img src="${fullLogoUrl}" alt="Logo" onerror="this.onerror=null; this.src='${fallbackLogoUrl}';" />
              </div>
              <div>
                <div class="brand-name">${companyName}</div>
                <div class="brand-sub">EV Charging Services</div>
              </div>
            </div>
            <div class="inv-title">
              <h1>INVOICE</h1>
              <div class="inv-meta">
                <div><span class="k">Invoice No</span><span class="v">${transaction.transactionId}</span></div>
                <div><span class="k">Date</span><span class="v">${invoiceDateDisplay}</span></div>
                <div><span class="k">Status</span><span class="badge ${statusClass}">${statusLabel}</span></div>
              </div>
            </div>
          </div>

          <div class="rule"></div>

          <div class="cards">
            <div class="card">
              <h3>Charging Session</h3>
              <div class="row"><span class="lbl">Charger</span><span class="val">${transaction.chargerName || 'N/A'}</span></div>
              <div class="row"><span class="lbl">Connector</span><span class="val">${transaction.connectorId || 'N/A'}</span></div>
              <div class="row"><span class="lbl">Start</span><span class="val">${startTime}</span></div>
              <div class="row"><span class="lbl">End</span><span class="val">${stopTime}</span></div>
            </div>
            <div class="card">
              <h3>Summary</h3>
              <div class="row"><span class="lbl">Energy Consumed</span><span class="val">${kwConsumed.toFixed(3)} kWh</span></div>
              <div class="row"><span class="lbl">Rate (excl. GST)</span><span class="val">${rateDisplay}${transaction.pricePerKw ? ' / kWh' : ''}</span></div>
              <div class="row"><span class="lbl">GST</span><span class="val">${taxPct}%</span></div>
              <div class="row"><span class="lbl">Total (incl. GST)</span><span class="val">${totalAmount ? '₹' + totalAmount : '₹0.00'}</span></div>
            </div>
          </div>

          <table class="items">
            <thead>
              <tr>
                <th>Description</th>
                <th class="num">Qty</th>
                <th class="num">Rate</th>
                <th class="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div class="item-desc">EV Charging${transaction.chargerName ? ' — ' + transaction.chargerName : ''}</div>
                  <div class="item-sub">Connector ${transaction.connectorId || 'N/A'} &middot; Energy consumed</div>
                </td>
                <td class="num">${kwConsumed.toFixed(3)} kWh</td>
                <td class="num">${rateDisplay}</td>
                <td class="num">${baseAmount ? '₹' + baseAmount : '—'}</td>
              </tr>
            </tbody>
          </table>

          <div class="totals">
            <div class="totals-box">
              ${baseAmount ? `<div class="row"><span class="lbl">Subtotal</span><span class="val">₹${baseAmount}</span></div>` : ''}
              ${gstAmount ? `<div class="row"><span class="lbl">GST (${taxPct}%)</span><span class="val">₹${gstAmount}</span></div>` : ''}
              <div class="row total"><span class="lbl">Total</span><span class="val">${totalAmount ? '₹' + totalAmount : '₹0.00'}</span></div>
              ${amountPaid > 0 ? `<div class="row paid"><span class="lbl">Amount Paid</span><span class="val">₹${amountPaid.toFixed(2)}</span></div>` : ''}
              ${amountRemaining !== null ? `<div class="row refund"><span class="lbl">Amount Refunded</span><span class="val">₹${amountRemaining}</span></div>` : ''}
            </div>
          </div>

          ${isUpi ? `
          <div class="pay">
            <h3>Payment Details</h3>
            <div class="pay-grid">
              <div class="pay-item"><span class="lbl">Method: </span><span class="val">UPI (Paytm)</span></div>
              ${paymentRef ? `<div class="pay-item"><span class="lbl">UPI Transaction ID: </span><span class="val">${paymentRef}</span></div>` : ''}
              ${orderRef ? `<div class="pay-item"><span class="lbl">Order ID: </span><span class="val">${orderRef}</span></div>` : ''}
            </div>
          </div>` : ''}

          <div class="foot">
            <div class="divider"></div>
            <div class="thanks">Thank you for choosing ${companyName}!</div>
            ${supportEmail ? `<div>For support, contact <strong>${supportEmail}</strong></div>` : ''}
            <div>This is a computer-generated invoice and does not require a signature.</div>
          </div>
        </div>
      </body>
      </html>
    `;

    // Create a temporary div to render the invoice (better for mobile)
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '0';
    tempDiv.style.width = '210mm';
    tempDiv.style.minHeight = '297mm';
    tempDiv.style.background = 'white';
    tempDiv.innerHTML = invoiceHTML;
    document.body.appendChild(tempDiv);

    // Wait for content and images to load
    const generatePDF = () => {
      try {
        // Load html2canvas dynamically if not available
        const loadHtml2Canvas = () => {
          return new Promise((resolve, reject) => {
            if (window.html2canvas) {
              resolve(window.html2canvas);
              return;
            }

            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
            script.onload = () => resolve(window.html2canvas);
            script.onerror = () => reject(new Error('Failed to load html2canvas'));
            document.head.appendChild(script);
          });
        };

        loadHtml2Canvas().then((html2canvas) => {
          // Use the container div instead of body for better mobile compatibility
          const container = tempDiv.querySelector('.invoice-container') || tempDiv;
          return html2canvas(container, {
            scale: 2,
            useCORS: true,
            logging: false,
            allowTaint: true,
            backgroundColor: '#ffffff',
            width: container.scrollWidth || 794, // A4 width in pixels at 96dpi
            height: container.scrollHeight || 1123, // A4 height in pixels
            windowWidth: container.scrollWidth || 794,
            windowHeight: container.scrollHeight || 1123,
          });
        }).then((canvas) => {
          const imgData = canvas.toDataURL('image/png', 1.0);
          const pdf = new jsPDF('p', 'mm', 'a4');
          const imgWidth = 210; // A4 width in mm
          const pageHeight = 297; // A4 height in mm
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          // The invoice is laid out to fill one A4 page (min-height: 297mm), so
          // sub-pixel rounding in html2canvas can make imgHeight a fraction taller
          // than a page and spill a near-empty page. Only add another page when the
          // leftover is a meaningful amount of content, not a rounding sliver.
          const pageOverflowTolerance = 10; // mm
          let heightLeft = imgHeight;
          let position = 0;

          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;

          while (heightLeft > pageOverflowTolerance) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
          }

          pdf.save(`Invoice_${transaction.transactionId}.pdf`);
          document.body.removeChild(tempDiv);
        }).catch((error) => {
          fallbackPrint();
        });
      } catch (error) {
        fallbackPrint();
      }
    };

    const fallbackPrint = () => {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(invoiceHTML);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
      document.body.removeChild(tempDiv);
    };

    // Wait for images to load before generating PDF
    setTimeout(() => {
      const images = tempDiv.querySelectorAll('img');
      let imagesLoaded = 0;
      const totalImages = images.length;

      const checkAndGenerate = () => {
        if (imagesLoaded === totalImages || totalImages === 0) {
          setTimeout(generatePDF, 800); // Longer delay for mobile rendering
        }
      };

      if (totalImages > 0) {
        images.forEach((img) => {
          if (img.complete && img.naturalHeight !== 0) {
            imagesLoaded++;
            checkAndGenerate();
          } else {
            img.onload = () => {
              imagesLoaded++;
              checkAndGenerate();
            };
            img.onerror = () => {
              imagesLoaded++;
              checkAndGenerate();
            };
            // Timeout for images that never load
            setTimeout(() => {
              if (!img.complete) {
                imagesLoaded++;
                checkAndGenerate();
              }
            }, 3000);
          }
        });
      } else {
        setTimeout(generatePDF, 800);
      }
    }, 300); // Initial delay to ensure DOM is ready
  };

  // Fetch a completed direct-UPI (Paytm) transaction by its UPI transaction id
  // (UTR) and download its invoice. The lookup returns price/tax on the
  // transaction itself, so the invoice is generated with a null connector.
  const handleDownloadUpiInvoice = async () => {
    const value = upiInvoiceId.trim();
    if (!value) {
      setUpiInvoiceError('Please enter your UPI transaction id');
      return;
    }
    setUpiInvoiceError('');
    setUpiInvoiceLoading(true);
    try {
      const data = await getInvoiceByUpiTransactionId(value);
      if (data && data.success && data.data) {
        if (data.data.isActive) {
          setUpiInvoiceError('Invoice not available. Transaction still active.');
        } else {
          handleDownloadInvoice(data.data, null);
        }
      } else {
        setUpiInvoiceError(data?.message || 'No invoice found for this UPI transaction id.');
      }
    } catch (err) {
      setUpiInvoiceError(err.message || 'Failed to fetch invoice. Please try again.');
    } finally {
      setUpiInvoiceLoading(false);
    }
  };

  return (
    <RootStyle title="Active Transaction">
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
          py: { xs: 1, sm: 2, md: 3 },
          px: { xs: 1, sm: 2, md: 3 }
        }}
      >

        {loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <CircularProgress
                size={60}
                thickness={4}
                sx={{
                  color: theme.palette.primary.main,
                  animationDuration: '1.5s',
                }}
              />
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <CircularProgress
                size={80}
                thickness={4}
                sx={{
                  color: theme.palette.primary.main,
                  animationDuration: '1.5s',
                }}
              />
            </Box>
            <Typography
              variant="h6"
              sx={{
                mt: 2,
                color: 'text.secondary',
                fontWeight: 500,
                letterSpacing: '0.5px',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                textAlign: 'center',
                px: 2,
              }}
            >
              Loading transaction details...
            </Typography>
          </Box>
        )}

        {/* Charger ID input form — shown when no chargerId in URL */}
        {!loading && !hasSearched && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '60vh',
              px: { xs: 1, sm: 2 },
            }}
          >
            <SearchCard sx={{ maxWidth: 480, width: '100%' }}>
              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                {/* Logo */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <img
                    src={logoUrl}
                    alt="Logo"
                    onError={(e) => { e.target.onerror = null; e.target.src = '/logo/amikCharge.svg'; }}
                    style={{ maxWidth: '140px', maxHeight: '40px', height: 'auto', display: 'block' }}
                  />
                </Box>

                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, mb: 1, color: theme.palette.primary.main, textAlign: 'center' }}
                >
                  View Active Transaction
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3, textAlign: 'center', lineHeight: 1.6, fontSize: { xs: '0.9375rem', sm: '1rem' } }}
                >
                  Enter a Charger ID to view the active transaction details for that charger.
                </Typography>

                <TextField
                  fullWidth
                  label="Charger ID"
                  placeholder="e.g. CHARGER_001"
                  value={chargerIdInput}
                  onChange={(e) => { setChargerIdInput(e.target.value); setChargerIdInputError(''); }}
                  error={!!chargerIdInputError}
                  helperText={chargerIdInputError || ' '}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleChargerSearch(); }}
                  size="medium"
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleChargerSearch}
                  sx={{
                    fontWeight: 700,
                    borderRadius: 2,
                    py: 1.5,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': { boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}` },
                  }}
                >
                  Search
                </Button>
              </CardContent>
            </SearchCard>
          </Box>
        )}

        {!loading && hasSearched && (error || chargerDetails || chargerLoading) && (
          <SearchCard>
            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 }, textAlign: 'center', position: 'relative' }}>
              {/* Card Header (Logo & Refresh) */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: { xs: 2.5, sm: 3 },
                  position: 'relative',
                  width: '100%',
                  minHeight: '40px',
                  zIndex: 20
                }}
              >
                {/* Logo Section */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <img
                    src={logoUrl}
                    alt="Logo"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/logo/amikCharge.svg';
                    }}
                    style={{
                      width: 'auto',
                      maxWidth: '120px',
                      maxHeight: '25px',
                      height: 'auto',
                      display: 'block',
                      backgroundColor: 'transparent',
                      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08))',
                    }}
                  />
                </Box>

                {/* Manual Refresh Control */}
                {chargerDetails && (
                  <Box
                    sx={{
                      background: alpha(theme.palette.background.paper, 0.9),
                      backdropFilter: 'blur(8px)',
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      borderRadius: '24px',
                      pl: { xs: 1.25, sm: 2 },
                      pr: 0.5,
                      py: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 6px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    {isRefreshing && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={16} thickness={6} sx={{ color: 'primary.main' }} />
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'primary.main',
                                fontWeight: 700,
                                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                letterSpacing: '0.3px',
                                textTransform: 'uppercase'
                              }}
                            >
                              Refreshing...
                            </Typography>
                          </Stack>
                        </Box>

                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            mx: 0.5,
                            height: 16,
                            alignSelf: 'center',
                            borderColor: alpha(theme.palette.divider, 0.6)
                          }}
                        />
                      </>
                    )}

                    <Tooltip title="Refresh Now">
                      <IconButton
                        size="small"
                        onClick={refreshData}
                        disabled={isRefreshing}
                        sx={{
                          color: 'primary.main',
                          width: { xs: 28, sm: 32 },
                          height: { xs: 28, sm: 32 },
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                            transform: 'rotate(180deg)',
                          },
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        <Icon icon="eva:refresh-fill" width={20} height={20} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              {/* Error Message */}
              {error && !chargerLoading && !chargerDetails && (
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {error}
                  </Alert>
                </Box>
              )}

              {/* Charger Details Section — initial load spinner */}
              {chargerLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, mb: 2 }}>
                  <CircularProgress size={40} />
                </Box>
              )}

              {chargerDetails && !chargerLoading && (
                <Box sx={{ mt: 3 }}>
                  {/* Header */}
                  <Box sx={{ mb: 3 }}>
                    <Divider
                      sx={{
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                        borderWidth: 1,
                      }}
                    />
                  </Box>

                  {/* Charger Information Card */}
                  <DetailCard sx={{ mb: 3, boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}` }}>
                    <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 3.5 } }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                        <Box
                          sx={{
                            width: { xs: 32, sm: 36 },
                            height: { xs: 32, sm: 36 },
                            borderRadius: 1.5,
                            background: alpha(theme.palette.primary.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Icon
                            icon={batteryFill}
                            width={18}
                            height={18}
                            style={{ color: theme.palette.primary.main }}
                          />
                        </Box>
                        <Box sx={{ flex: 1, textAlign: 'left' }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                              fontSize: { xs: '1rem', sm: '1.125rem' },
                              textAlign: 'left',
                            }}
                          >
                            Charger Address
                          </Typography>
                          {chargerDetails.street && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                display: 'block',
                              }}
                            >
                              {chargerDetails.street}
                            </Typography>
                          )}
                          {chargerDetails.locationLatitude && chargerDetails.locationLongitude && (
                            <Typography
                              component="a"
                              href={`https://www.google.com/maps/dir/?api=1&destination=${chargerDetails.locationLatitude},${chargerDetails.locationLongitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="caption"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.5,
                                color: 'primary.main',
                                fontWeight: 600,
                                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              <Icon icon={pinFill} width={16} height={16} />
                              Get Directions
                            </Typography>
                          )}
                        </Box>
                      </Stack>

                      <Grid container spacing={{ xs: 1.5, sm: 2.5 }}>
                        {(chargerDetails.chargepoint_name || chargerDetails.name) && (
                          <Grid item xs={6} sm={6}>
                            <Box
                              sx={{
                                p: { xs: 1.5, sm: 2 },
                                borderRadius: 2,
                                background: alpha(theme.palette.primary.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                height: '100%',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                  borderColor: alpha(theme.palette.primary.main, 0.3),
                                },
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '1px',
                                  display: 'block',
                                  mb: 1,
                                }}
                              >
                                Charger Name
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.primary',
                                  fontSize: { xs: '0.9375rem', sm: '1rem' },
                                  fontWeight: 600,
                                }}
                              >
                                {chargerDetails.chargepoint_name || chargerDetails.name}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {chargerDetails.status && (
                          <Grid item xs={6} sm={6}>
                            <Box
                              sx={{
                                p: { xs: 1.5, sm: 2 },
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                                border: '1px solid rgba(102, 126, 234, 0.15)',
                                height: '100%',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                                  borderColor: 'rgba(102, 126, 234, 0.3)',
                                },
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '1px',
                                  display: 'block',
                                  mb: 1,
                                }}
                              >
                                Status
                              </Typography>
                              <Chip
                                label={
                                  chargerDetails.status === 'A' ? 'Active' :
                                    chargerDetails.status === 'I' ? 'Inactive' :
                                      chargerDetails.status
                                }
                                size="medium"
                                color={
                                  chargerDetails.status === 'A' ||
                                    chargerDetails.status?.toLowerCase() === 'available' ||
                                    chargerDetails.status?.toLowerCase() === 'active' ||
                                    chargerDetails.status?.toLowerCase() === 'online'
                                    ? 'success'
                                    : chargerDetails.status?.toLowerCase() === 'charging' ||
                                      chargerDetails.status?.toLowerCase() === 'occupied'
                                      ? 'warning'
                                      : chargerDetails.status === 'I' ||
                                        chargerDetails.status?.toLowerCase() === 'offline' ||
                                        chargerDetails.status?.toLowerCase() === 'unavailable' ||
                                        chargerDetails.status?.toLowerCase() === 'inactive'
                                        ? 'error'
                                        : 'default'
                                }
                                sx={{
                                  fontWeight: 600,
                                  fontSize: { xs: '0.9375rem', sm: '1rem' },
                                  height: { xs: 28, sm: 32 },
                                  minWidth: { xs: 90, sm: 110 },
                                }}
                              />
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </DetailCard>

                  {/* Tabs for Connectors — unified tab card */}
                  {chargerDetails.connectors && chargerDetails.connectors.length > 0 && (() => {
                    const accentPalette = [
                      { main: '#0d47a1', light: '#e3f0fd' }, // Connector 1 — deep blue
                      { main: '#1b5e20', light: '#e6f4e9' }, // Connector 2 — deep green
                      { main: '#6a1b9a', light: '#f3e5f5' }, // 3 — purple
                      { main: '#e65100', light: '#fff3e0' }, // 4 — orange
                    ];
                    const getAccent = (cId) => accentPalette[
                      (Number(cId) - 1 + accentPalette.length) % accentPalette.length
                    ] || accentPalette[0];
                    const sortedConnectors = [...chargerDetails.connectors].sort((a, b) => {
                      const aId = a.connector_id || a.connectorId || a.id || 0;
                      const bId = b.connector_id || b.connectorId || b.id || 0;
                      return Number(aId) - Number(bId);
                    });
                    const activeConnector = sortedConnectors[selectedTab] || sortedConnectors[0];
                    const activeConnectorId = activeConnector.connector_id || activeConnector.connectorId || activeConnector.id || 1;
                    const selectedAccent = getAccent(activeConnectorId);
                    const chargerStatusUpper = chargerDetails.status?.toString().toUpperCase();
                    const getStatusColor = (status) => {
                      const s = status?.toLowerCase();
                      if (s === 'n/a' || s === 'na') return 'default';
                      if (s === 'available') return 'success';
                      if (s === 'in use' || s === 'charging' || s === 'preparing') return 'warning';
                      if (s === 'unavailable' || s === 'not available' || s === 'faulted') return 'error';
                      return 'default';
                    };

                    return (
                      <Box sx={{ mt: 4 }}>
                        <TabContext value={selectedTab.toString()}>
                          <Card
                            elevation={0}
                            sx={{
                              borderRadius: 2,
                              overflow: 'hidden',
                              border: `2px solid ${selectedAccent.main}`,
                              boxShadow: `0 6px 20px ${alpha(selectedAccent.main, 0.18)}`,
                              transition: 'border-color 0.3s, box-shadow 0.3s',
                              backgroundColor: '#fff',
                            }}
                          >
                            <Box
                              role="tablist"
                              aria-label="Connector tabs"
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                borderBottom: `2px solid ${selectedAccent.main}`,
                              }}
                            >
                              {sortedConnectors.map((connector, index) => {
                                const connectorId = connector.connector_id || connector.connectorId || connector.id || index + 1;
                                const connectorStatus = (chargerStatusUpper === 'N/A' || chargerStatusUpper === 'NA')
                                  ? 'N/A'
                                  : (connector.status || connector.connectorStatus || 'Unknown');
                                const isSelected = selectedTab === index;
                                const accent = getAccent(connectorId);
                                const isLast = index === sortedConnectors.length - 1;
                                const handleSelect = () => setSelectedTab(index);
                                const handleKey = (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleSelect();
                                  }
                                };

                                return (
                                  <Box
                                    key={connectorId}
                                    role="tab"
                                    tabIndex={0}
                                    aria-selected={isSelected}
                                    onClick={handleSelect}
                                    onKeyDown={handleKey}
                                    sx={{
                                      flex: 1,
                                      minWidth: 0,
                                      cursor: 'pointer',
                                      userSelect: 'none',
                                      position: 'relative',
                                      px: { xs: 1, sm: 1.75 },
                                      pt: { xs: 1.25, sm: 1.5 },
                                      pb: { xs: 1.25, sm: 1.5 },
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: 0.5,
                                      minHeight: { xs: 76, sm: 84 },
                                      backgroundColor: isSelected ? accent.main : accent.light,
                                      color: isSelected ? '#fff' : accent.main,
                                      borderRight: !isLast ? `1px solid ${alpha('#000', 0.08)}` : 'none',
                                      transition: 'background-color 0.2s, color 0.2s',
                                      '&:hover': {
                                        backgroundColor: isSelected ? accent.main : alpha(accent.main, 0.18),
                                      },
                                      '&:focus-visible': {
                                        outline: `2px solid ${alpha(accent.main, 0.6)}`,
                                        outlineOffset: -2,
                                      },
                                    }}
                                  >
                                    {isSelected && (
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          bottom: 6,
                                          right: 8,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: { xs: 0, sm: 0.5 },
                                          px: { xs: 0.25, sm: 0.75 },
                                          py: 0.25,
                                          borderRadius: 10,
                                          backgroundColor: 'rgba(255,255,255,0.22)',
                                          backdropFilter: 'blur(4px)',
                                        }}
                                      >
                                        <Icon
                                          icon={checkmarkCircle2Fill}
                                          width={14}
                                          height={14}
                                          style={{ color: '#fff' }}
                                        />
                                        <Typography
                                          component="span"
                                          sx={{
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            color: '#fff',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            lineHeight: 1,
                                            display: { xs: 'none', sm: 'inline' },
                                          }}
                                        >
                                          Selected
                                        </Typography>
                                      </Box>
                                    )}
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1rem', sm: '1.25rem' },
                                        letterSpacing: { xs: '0.4px', sm: '0.6px' },
                                        textTransform: 'uppercase',
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      Connector {connectorId}
                                    </Typography>
                                    <Chip
                                      label={connectorStatus}
                                      size="small"
                                      color={getStatusColor(connectorStatus)}
                                      sx={{
                                        height: 24,
                                        fontSize: '0.8125rem',
                                        fontWeight: 700,
                                        backgroundColor: isSelected ? 'rgba(255,255,255,0.95)' : undefined,
                                        color: isSelected ? accent.main : undefined,
                                        '& .MuiChip-label': { px: 1 },
                                      }}
                                    />
                                  </Box>
                                );
                              })}
                            </Box>

                            {/* Tab Panels */}
                            {sortedConnectors.map((connector, index) => {
                              const connectorId = connector.connector_id || connector.connectorId || connector.id || index + 1;
                              const isLoading = transactionLoading[connectorId];
                              return (
                                <TabPanel
                                  key={connectorId}
                                  value={index.toString()}
                                  sx={{
                                    p: 0,
                                    '&.MuiTabPanel-root': {
                                      padding: { xs: '16px', sm: '20px' },
                                      backgroundColor: '#fff',
                                    }
                                  }}
                                >
                                  {renderConnectorContent(connector, connectorId, isLoading)}
                                </TabPanel>
                              );
                            })}
                          </Card>
                        </TabContext>
                      </Box>
                    );
                  })()}

                  {/* Single Connector Status (if connectors array not available but connector info exists) */}
                  {(!chargerDetails.connectors || chargerDetails.connectors.length === 0) &&
                    (chargerDetails.connectorId || chargerDetails.connectorStatus) && (
                      <DetailCard>
                        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                            <Box
                              sx={{
                                width: { xs: 40, sm: 48 },
                                height: { xs: 40, sm: 48 },
                                borderRadius: 1.5,
                                background: alpha(theme.palette.primary.main, 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Icon
                                icon={flashFill}
                                width={24}
                                height={24}
                                style={{ color: theme.palette.primary.main }}
                              />
                            </Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                fontSize: { xs: '1rem', sm: '1.125rem' },
                                textAlign: 'left',
                              }}
                            >
                              Connector Status
                            </Typography>
                          </Stack>

                          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                            {chargerDetails.connectorId && (
                              <Grid item xs={12} sm={6}>
                                <GridLabel>
                                  <Box className="label">Connector ID</Box>
                                  <Box className="value">{chargerDetails.connectorId}</Box>
                                </GridLabel>
                              </Grid>
                            )}
                            {chargerDetails.connectorStatus && (
                              <Grid item xs={12} sm={6}>
                                <GridLabel>
                                  <Box className="label">Connector Status</Box>
                                  <Box className="value" sx={{ mt: 0.5 }}>
                                    <Chip
                                      label={chargerDetails.connectorStatus}
                                      size="small"
                                      color={
                                        chargerDetails.connectorStatus?.toLowerCase() === 'available'
                                          ? 'success'
                                          : chargerDetails.connectorStatus?.toLowerCase() === 'charging' ||
                                            chargerDetails.connectorStatus?.toLowerCase() === 'preparing'
                                            ? 'warning'
                                            : chargerDetails.connectorStatus?.toLowerCase() === 'unavailable' ||
                                              chargerDetails.connectorStatus?.toLowerCase() === 'faulted'
                                              ? 'error'
                                              : 'default'
                                      }
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                        height: { xs: 24, sm: 28 },
                                        minWidth: { xs: 90, sm: 100 },
                                      }}
                                    />
                                  </Box>
                                </GridLabel>
                              </Grid>
                            )}
                          </Grid>
                        </CardContent>
                      </DetailCard>
                    )}
                </Box>
              )}
            </CardContent>
          </SearchCard>
        )}

        {/* Download invoice by UPI transaction id — for direct-UPI Paytm payments */}
        {!loading && (
          <SearchCard sx={{ mt: { xs: 2, sm: 3 }, maxWidth: 680, mx: 'auto' }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 }, textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  p: 1.25,
                  mb: 1.5,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                }}
              >
                <Icon icon={downloadFill} width={26} height={26} color={theme.palette.primary.main} />
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: theme.palette.primary.main }}>
                Download your invoice
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2.5,
                  mx: 'auto',
                  maxWidth: 520,
                  lineHeight: 1.7,
                  fontSize: { xs: '0.9375rem', sm: '1rem' },
                }}
              >
                Paid directly via UPI? Enter the <strong>UPI transaction ID</strong> (the UTR / reference
                number from your UPI or bank app) to download the invoice for that charging session.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                justifyContent="center"
                alignItems="flex-start"
                sx={{ maxWidth: 520, mx: 'auto' }}
              >
                <TextField
                  fullWidth
                  label="UPI Transaction ID"
                  placeholder="e.g. 123456789012"
                  value={upiInvoiceId}
                  onChange={(e) => { setUpiInvoiceId(e.target.value); setUpiInvoiceError(''); }}
                  error={!!upiInvoiceError}
                  helperText={upiInvoiceError || ' '}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleDownloadUpiInvoice(); }}
                  disabled={upiInvoiceLoading}
                  size="medium"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleDownloadUpiInvoice}
                  disabled={upiInvoiceLoading}
                  startIcon={
                    upiInvoiceLoading
                      ? <CircularProgress size={18} color="inherit" />
                      : <Icon icon={downloadFill} width={18} height={18} />
                  }
                  sx={{
                    fontWeight: 700,
                    borderRadius: 2,
                    py: 1.5,
                    px: 3,
                    whiteSpace: 'nowrap',
                    minWidth: { xs: '100%', sm: 180 },
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': { boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}` },
                  }}
                >
                  {upiInvoiceLoading ? 'Fetching...' : 'Download'}
                </Button>
              </Stack>
            </CardContent>
          </SearchCard>
        )}

        {/* Download app promo — link UPI ID to track direct-UPI transactions */}
        {!loading && (
          <SearchCard sx={{ mt: { xs: 2, sm: 3 }, maxWidth: 680, mx: 'auto' }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 }, textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  p: 1.25,
                  mb: 1.5,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                }}
              >
                <Icon icon={flashFill} width={26} height={26} color={theme.palette.primary.main} />
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: theme.palette.primary.main }}>
                Track your charging on the go
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2.5,
                  mx: 'auto',
                  maxWidth: 520,
                  lineHeight: 1.7,
                  fontSize: { xs: '0.9375rem', sm: '1rem' },
                }}
              >
                Paid directly via UPI? Download the <strong>Amik Charge</strong> app and link your UPI ID
                to view live charging status and your complete transaction history — anytime, anywhere.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                justifyContent="center"
                alignItems="center"
              >
                <Box
                  component="a"
                  href="https://apps.apple.com/in/app/amik-charge/id6482231255"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download Amik Charge on the App Store"
                  sx={{ display: 'inline-flex', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}
                >
                  <img
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    alt="Download on the App Store"
                    style={{ height: 46, width: 'auto', display: 'block' }}
                  />
                </Box>
                <Box
                  component="a"
                  href="https://play.google.com/store/apps/details?id=com.in.amik"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get Amik Charge on Google Play"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    height: 46,
                    px: 1.75,
                    bgcolor: '#000',
                    color: '#fff',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.3)',
                    textDecoration: 'none',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-2px)' },
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                    <path fill="#00E1FF" d="M3.063 2.062a1.5 1.5 0 0 0-.563 1.176v17.524a1.5 1.5 0 0 0 .563 1.176l.06.056L13.02 12.06v-.12L3.123 2.006l-.06.056z" />
                    <path fill="#FFCE00" d="m16.32 15.36-3.3-3.3v-.12l3.3-3.3.075.042 3.912 2.223c1.117.634 1.117 1.673 0 2.31l-3.912 2.223-.075.042z" />
                    <path fill="#FF3A44" d="m16.395 15.318-3.375-3.378L3.063 21.9a1.22 1.22 0 0 0 1.557.045l11.775-6.627z" />
                    <path fill="#00F076" d="M16.395 8.682 4.62 2.055A1.22 1.22 0 0 0 3.063 2.1l9.957 9.84 3.375-3.258z" />
                  </svg>
                  <Box sx={{ textAlign: 'left', lineHeight: 1 }}>
                    <Typography sx={{ fontSize: '0.5rem', letterSpacing: '0.5px', textTransform: 'uppercase', opacity: 0.9 }}>
                      Get it on
                    </Typography>
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, mt: '2px', fontFamily: 'Roboto, Arial, sans-serif' }}>
                      Google Play
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </SearchCard>
        )}


        {/* Stop Transaction Dialog — Phone + OTP verification */}
        <Dialog
          open={stopDialogOpen}
          onClose={handleStopCancel}
          aria-labelledby="stop-dialog-title"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
              m: { xs: 1, sm: 2 },
              maxHeight: { xs: '90vh', sm: 'auto' },
            }
          }}
        >
          <DialogTitle
            id="stop-dialog-title"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              pb: { xs: 1.5, sm: 2 },
              px: { xs: 2, sm: 3 },
              pt: { xs: 2, sm: 3 },
              color: theme.palette.error.main,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Icon icon={phoneCallFill} width={24} height={24} />
            <Typography sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 700 }}>
              Verify to Stop Transaction
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
            <DialogContentText
              sx={{
                fontSize: { xs: '0.9375rem', sm: '1rem' },
                color: 'text.secondary',
                mb: { xs: 2, sm: 3 },
                lineHeight: 1.6,
              }}
            >
              {!stopOtpSent
                ? 'Enter your registered mobile number to receive an OTP for verification before stopping the transaction.'
                : `OTP sent to +91 ${stopPhoneNumber}. Enter the 6-digit code below to stop the transaction.`}
            </DialogContentText>

            {!stopOtpSent ? (
              <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  placeholder="Enter 10-digit mobile number"
                  value={stopPhoneNumber}
                  onChange={handleStopPhoneChange}
                  error={!!stopPhoneError}
                  helperText={stopPhoneError || 'Enter your 10-digit mobile number'}
                  disabled={sendingStopOtp}
                  size="small"
                  inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: { xs: '1rem', sm: '1.125rem' } },
                    '& .MuiInputLabel-root': { fontSize: { xs: '1rem', sm: '1.125rem' } },
                    '& .MuiFormHelperText-root': { fontSize: { xs: '0.9375rem', sm: '1rem' } },
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                <Alert severity="info" sx={{ borderRadius: 2, mb: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                    OTP sent to +91 {stopPhoneNumber}. Please enter the 6-digit code below.
                  </Typography>
                </Alert>
                <TextField
                  fullWidth
                  label="Enter OTP"
                  placeholder="Enter 6-digit OTP"
                  value={stopOtp}
                  onChange={handleStopOtpChange}
                  error={!!stopOtpError}
                  helperText={stopOtpError || 'Enter the 6-digit OTP sent to your mobile number'}
                  disabled={verifyingStopOtp}
                  size="small"
                  inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: { xs: '1rem', sm: '1.125rem' } },
                    '& .MuiInputLabel-root': { fontSize: { xs: '1rem', sm: '1.125rem' } },
                    '& .MuiFormHelperText-root': { fontSize: { xs: '0.9375rem', sm: '1rem' } },
                  }}
                />
              </Box>
            )}

            {stopError && (
              <Alert severity="error" sx={{ mt: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                <Typography sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                  {stopError}
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{
            p: { xs: 2, sm: 3 },
            pt: { xs: 1, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            '& > *': { width: { xs: '100%', sm: 'auto' } }
          }}>
            <Button
              onClick={handleStopCancel}
              disabled={sendingStopOtp || verifyingStopOtp}
              variant="outlined"
              sx={{
                fontWeight: 600,
                px: { xs: 2, sm: 3 },
                py: { xs: 1, sm: 1.25 },
                fontSize: { xs: '1rem', sm: '1.125rem' },
                order: { xs: 2, sm: 1 },
              }}
            >
              Cancel
            </Button>
            {!stopOtpSent ? (
              <Button
                onClick={handleStopSendOTP}
                color="error"
                variant="contained"
                disabled={sendingStopOtp || !stopPhoneNumber || stopPhoneNumber.length !== 10}
                startIcon={sendingStopOtp ? <CircularProgress size={16} color="inherit" /> : <Icon icon={phoneCallFill} width={18} height={18} />}
                sx={{
                  fontWeight: 700,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.25 },
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
                  '&:hover': { boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)' },
                  order: { xs: 1, sm: 2 },
                }}
              >
                {sendingStopOtp ? 'Sending...' : 'Send OTP'}
              </Button>
            ) : (
              <Button
                onClick={handleStopVerifyAndStop}
                color="error"
                variant="contained"
                disabled={verifyingStopOtp || !stopOtp || stopOtp.length !== 6}
                startIcon={verifyingStopOtp ? <CircularProgress size={16} color="inherit" /> : <Icon icon={stopCircleFill} width={18} height={18} />}
                sx={{
                  fontWeight: 700,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.25 },
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
                  '&:hover': { boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)' },
                  order: { xs: 1, sm: 2 },
                }}
              >
                {verifyingStopOtp ? 'Verifying & Stopping...' : 'Verify & Stop'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Notification Setup Dialog */}
        <Dialog
          open={notificationDialogOpen}
          onClose={handleNotificationClose}
          aria-labelledby="notification-dialog-title"
          aria-describedby="notification-dialog-description"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
              m: { xs: 1, sm: 2 },
              maxHeight: { xs: '90vh', sm: 'auto' },
            }
          }}
        >
          <DialogTitle
            id="notification-dialog-title"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              pb: { xs: 1.5, sm: 2 },
              px: { xs: 2, sm: 3 },
              pt: { xs: 2, sm: 3 },
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Icon icon={bellFill} width={24} height={24} />
            <Typography sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Receive Charging Updates
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
            <DialogContentText
              id="notification-dialog-description"
              sx={{
                textAlign: 'left',
                fontSize: { xs: '0.9375rem', sm: '1rem' },
                color: 'text.secondary',
                mb: { xs: 2, sm: 3 },
                lineHeight: 1.6,
              }}
            >
              Enter your contact information to receive real-time updates about your charging session on Connector {notificationConnectorId}.
            </DialogContentText>

            {notificationSuccess ? (
              <Alert severity="success" sx={{ borderRadius: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                  Notification setup successful! You will now receive charging updates on your email.
                </Typography>
              </Alert>
            ) : (
              <>
                {!otpSent ? (
                  <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      placeholder="Enter your email address (e.g., user@example.com)"
                      value={email}
                      onChange={handleEmailChange}
                      error={!!emailError}
                      helperText={emailError || 'Enter a valid email address'}
                      disabled={sendingOtp}
                      type="email"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontSize: { xs: '1rem', sm: '1.125rem' },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '1rem', sm: '1.125rem' },
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: { xs: '0.9375rem', sm: '1rem' },
                        },
                      }}
                      inputProps={{
                        maxLength: 100,
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                    <Alert severity="info" sx={{ borderRadius: 2, mb: { xs: 1.5, sm: 2 } }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                        OTP has been sent to {email}. Please enter the 6-digit code below.
                      </Typography>
                    </Alert>
                    <TextField
                      fullWidth
                      label="Enter OTP"
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={handleOTPChange}
                      error={!!otpError}
                      helperText={otpError || 'Enter the 6-digit OTP sent to your email'}
                      disabled={verifyingOtp}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontSize: { xs: '1rem', sm: '1.125rem' },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '1rem', sm: '1.125rem' },
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: { xs: '0.9375rem', sm: '1rem' },
                        },
                      }}
                      inputProps={{
                        maxLength: 6,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                      }}
                    />
                  </Box>
                )}

                {notificationError && (
                  <Alert severity="error" sx={{ mt: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                    <Typography sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                      {notificationError}
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{
            p: { xs: 2, sm: 3 },
            pt: { xs: 1, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            '& > *': {
              width: { xs: '100%', sm: 'auto' },
            }
          }}>
            {!notificationSuccess && (
              <>
                <Button
                  onClick={handleNotificationClose}
                  disabled={sendingOtp || verifyingOtp}
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.25 },
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    order: { xs: 2, sm: 1 },
                  }}
                >
                  Cancel
                </Button>
                {!otpSent ? (
                  <Button
                    onClick={handleSendOTP}
                    color="primary"
                    variant="contained"
                    disabled={sendingOtp || !email || email.trim() === ''}
                    startIcon={sendingOtp ? <CircularProgress size={16} color="inherit" /> : <Icon icon={bellFill} width={18} height={18} />}
                    sx={{
                      fontWeight: 700,
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1, sm: 1.25 },
                      fontSize: { xs: '1rem', sm: '1.125rem' },
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                      },
                      '&:disabled': {
                        backgroundColor: 'rgba(25, 118, 210, 0.3)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                      order: { xs: 1, sm: 2 },
                    }}
                  >
                    {sendingOtp ? 'Sending...' : 'Send OTP'}
                  </Button>
                ) : (
                  <Button
                    onClick={handleVerifyOTP}
                    color="primary"
                    variant="contained"
                    disabled={verifyingOtp || !otp || otp.length !== 6}
                    startIcon={verifyingOtp ? <CircularProgress size={16} color="inherit" /> : <Icon icon={checkmarkCircle2Fill} width={18} height={18} />}
                    sx={{
                      fontWeight: 700,
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1, sm: 1.25 },
                      fontSize: { xs: '1rem', sm: '1.125rem' },
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                      },
                      '&:disabled': {
                        backgroundColor: 'rgba(25, 118, 210, 0.3)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                      order: { xs: 1, sm: 2 },
                    }}
                  >
                    {verifyingOtp ? 'Verifying...' : 'Verify OTP'}
                  </Button>
                )}
              </>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </RootStyle>
  );
}

